import { CollectionViewer, DataSource } from '@angular/cdk/collections'
import { ChangeDetectorRef } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { Registration } from '@database/models/registration.model'
import { DatabaseService } from '@database/services/database.service'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { PersonaField } from '@personas/interfaces/persona-field.interface'
import { Persona } from '@personas/interfaces/persona.interface'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { debounceTime, map, take, takeUntil } from 'rxjs/operators'
import { BulkChildAction, countBulkChildSelection, resolveBulkChildIds } from './bulk-child-selection'
import { RegistrationData } from './registration-data.interface'
import { ApprovalStatus } from './registration-status.enum'
import { RegistrationColumnDefinition } from './registration.column-definition'
import { RegistrationsRequest } from './registrations.request.interface'
import { RegistrationsResponse } from './registrations.response.interface'
/**
 * Defines a registrations collection datasource
 */
export class RegistrationsDataSource extends DataSource<Registration> {
  /**
   * Minimal current-user context (provided by AuthService).
   * This datasource must not read from storage directly.
   */
  private readonly _userContext?: {
    isAdmin: boolean
    approvedBy: { name: string; uuid?: string; source: 'planning' } | null
    displayName: string
    email: string
  }
  /** Datasource persona */
  public form: any
  /** Datasource initialize request */
  public initializeRequest: RegistrationsRequest
  /** Whether the datasource is performing an async operation */
  public processing = false
  /** Column definition array */
  public columnDefinitions: RegistrationColumnDefinition[] = []
  /** Displayed columns array */
  public displayColumns: string[] = []
  /** Whether all entries are selected */
  public allSelected = false

  public childAllSelected = false

  public childForm: any
  /** Total registrations for the form */
  public registrationsCount = 0

  /** Datasource changed */
  public changed$: Subject<boolean> = new Subject()
  /** Stop all hot observables */
  private _destroy$: Subject<boolean> = new Subject()
  /** Clean up the datasource */
  public destroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  hasFilters: boolean

  /**
   * Last keyset cursor returned by the server for the most-recently
   * loaded page. Sent back on the next `loadMore()` so the backend can
   * use its O(perPage) cursor-mode query path instead of skip()+limit()
   * plus a redundant countDocuments(). Cleared on any filter/sort
   * change or any direct page jump (cursor only makes sense for
   * forward infinite-scroll).
   */
  private _lastSortKey: string | null = null
  /** Creates the datasource */
  constructor(
    private service: DatabaseService,
    form: any,
    private checkinSpot: string,
    private event: ServerRepresentedEvent,
    private cdr: ChangeDetectorRef,
    /** Datasource total registrations */ public count: number,
    private workspaceRoleService?: WorkspaceRoleService,
    userContext?: {
      isAdmin: boolean
      approvedBy: { name: string; uuid?: string; source: 'planning' } | null
      displayName: string
      email: string
    }
  ) {
    super()
    this._userContext = userContext
    // Initializes the form queries
    this.initializeForm(form)
  }

  /** registrations data observable */
  private _data$: BehaviorSubject<Registration[]> = null
  /** Registrations data array */
  public data: Registration[] = []

  public childData: Registration[] = []
  public childDatalength: number
  public childDataSource = new MatTableDataSource<Registration>()
  public currentParentId: string
  /** Parent organization field name from parent form definition */
  private _parentOrganizationSourceFieldName: string | null = null
  /** Dedicated child-table field key for parent organization value */
  private _parentOrganizationColumnField = '__parent_organization'

  /**
   * Initializes a new Registration Data Source for a specific event form collection
   */
  public initialize(registrationsRequest: RegistrationsRequest, fetchData = true, append = false) {
    this.processing = true
    // Keyset cursor is only valid for forward infinite-scroll (append).
    // Fresh loads (keyword search, status filters, page jump, tab switch)
    // must drop any prior sort_key — otherwise rows that sort "after" the
    // old cursor are filtered out even when they match the new query.
    if (!append) {
      registrationsRequest = { ...registrationsRequest, sortKey: undefined }
      this._lastSortKey = null
    }
    this.initializeRequest = registrationsRequest
    if (!this._data$) {
      this._data$ = new BehaviorSubject([])
    }
    if (!fetchData) {
      this.processing = false
      if (this.changed$) {
        this.changed$.next(null)
      }
      return
    }
    this.service
      .getEventRegData(registrationsRequest)
      .pipe(
        debounceTime(300),
        map((registrants: RegistrationsResponse) => {
          registrants.data.map((registrant: RegistrationData) => {
            // Child table should only render child payload fields.
            // Parent object is intentionally ignored except parent organization.
            registrant.fields = registrant.fields || {}

            // Special requirement: show parent's ORGANIZATION field in child view.
            if (this._parentOrganizationSourceFieldName) {
              const parentPayload = Array.isArray(registrant['parent']) ? registrant['parent'][0] : registrant['parent']
              const parentOrganizationValue = parentPayload?.fields?.[this._parentOrganizationSourceFieldName]
              if (parentOrganizationValue !== undefined && parentOrganizationValue !== null && parentOrganizationValue !== '') {
                registrant.fields[this._parentOrganizationColumnField] = parentOrganizationValue as any
              }
            }

            const cities = Object.entries(registrant.fields || {}).filter(
              ([key, value]) => value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'city')
            )
            if (!cities.length) return registrant
            cities.forEach(([key, value]) => {
              registrant.fields[key] = value['city']
            })
            return registrant
          })
          return registrants
        })
      )
      .subscribe(
        (registrations: RegistrationsResponse) => {
          if (append) {
            const newData = registrations.data ? Registration.createFromArray(registrations.data, this.service, this.checkinSpot, this._userContext) : []
            if (this.allSelected) {
              newData.forEach((o) => (o.selected = true))
            }
            newData.forEach((o) => o.changed$.pipe(takeUntil(this._destroy$)).subscribe(() => this.changed$.next(null)))
            this.data.push(...newData)
          } else {
            this.data = registrations.data ? Registration.createFromArray(registrations.data, this.service, this.checkinSpot, this._userContext) : []
            // Preserve visual selection when allSelected is active
            if (this.allSelected) {
              this.data.forEach((o) => (o.selected = true))
            }
            this.data.forEach((o) => o.changed$.pipe(takeUntil(this._destroy$)).subscribe(() => this.changed$.next(null)))
            this.registrationsCount = registrations.total
            this.initializeRequest.maxPages = registrations.last_page
          }
          // Capture keyset cursor for the next loadMore() so the backend
          // can stay on its O(perPage) fast-path. Falls back to null if
          // the server didn't return it (older deploys), in which case
          // loadMore() will silently use legacy page-based pagination.
          this._lastSortKey = registrations.last_sort_key || null
          if (this._data$) {
            this._data$.next(this.data)
          }
          this.processing = false
          if (this.changed$) {
            this.changed$.next(null)
          }
          if (registrations.current_page === 1) {
            this.service.getAvgRatingData(registrationsRequest.workspace, registrationsRequest.event, registrationsRequest.form).subscribe((res) => {
              this.columnDefinitions.forEach((col: RegistrationColumnDefinition) => {
                if (!res[col.field]) return
                if (col.header.split(' ').includes(`${res[col.field]})`)) return
                col.header = `${col.header} (Avg. ${res[col.field]})`
              })
              this.cdr.detectChanges()
            })
          }
        },
        (error) => {
          this.processing = false
          this.changed$.next(null)
          this.cdr.detectChanges()
        }
      )
  }
  /** displays certain fields first based on priority
   * @deprecated
   */
  public fieldsDisplayPriority() {}

  /** initializes the datasource collection table based on a persona */
  public initializeForm(form: Persona) {
    this.form = form
    this._parentOrganizationSourceFieldName = null
    this.displayColumns.push('#select', '#flag')
    // Only add barcode at the beginning for non-GROUP forms

    const addedFields = new Set<string>()
    const parentForm = form.type === PersonaTypes.MEMBER && form.parent ? this.service.forms?.value?.find((o) => o.id === form.parent) : null
    const parentOrganizationElement = parentForm?.form_elements?.find((o) => o.form_element_type_id === PersonaFieldTypes.ORGANIZATION)
    if (parentOrganizationElement?.name) {
      this._parentOrganizationSourceFieldName = parentOrganizationElement.name
    }

    form.form_elements
      .sort((formElementA: PersonaField, formElementB: PersonaField) => {
        return formElementA.weight - formElementB.weight
      })
      .forEach((formElement) => {
        if (
          formElement.form_element_type_id !== PersonaFieldTypes.INFORMATION_MESSAGE &&
          formElement.form_element_type_id !== PersonaFieldTypes.WARNING_MESSAGE &&
          formElement.form_element_type_id !== PersonaFieldTypes.LINE_SEPARATOR
        ) {
          const fieldName = formElement.name

          if (!addedFields.has(fieldName)) {
            this.columnDefinitions.push({
              header: formElement.label,
              field: fieldName
            })
            this.displayColumns.push(fieldName)
            addedFields.add(fieldName)
          }
        }
      })

    // Add parent organization column when available.
    if (this._parentOrganizationSourceFieldName && !addedFields.has(this._parentOrganizationColumnField)) {
      this.columnDefinitions.push({
        header: parentOrganizationElement?.label || parentOrganizationElement?.name || 'organization',
        field: this._parentOrganizationColumnField
      })
      this.displayColumns.push(this._parentOrganizationColumnField)
      addedFields.add(this._parentOrganizationColumnField)
    }

    if (form.type === PersonaTypes.GROUP) {
      this.displayColumns.push('#members', '#register_count')
    }
    this.displayColumns.push('#approve', '#approved_by')
    if (form.type !== PersonaTypes.GROUP) {
      this.displayColumns.push('#prints')
    }

    if (form.form_elements.find((o) => o.name === 'email')) {
      this.displayColumns.push('#email')
    }
    if (form.form_elements.find((o) => o.name === 'phone')) {
      this.displayColumns.push('#sms') // SMS column
      this.displayColumns.push('#whatsapp') // WhatsApp column
    }
    // Only add actions column if user can see it
    const canSeeActionsColumn = !this.workspaceRoleService || this.workspaceRoleService.canSeeActionsColumn()
    if (canSeeActionsColumn) {
      this.displayColumns.push('#actions')
    }

    if (form.type == PersonaTypes.GROUP) {
      this.displayColumns.push('#portal_link')
    }
    if (form.type !== PersonaTypes.GROUP) {
      this.displayColumns.push('#attend', '#created_at')
    }
    this.displayColumns.push('#barcode')

    if (form.type !== PersonaTypes.GROUP) {
      this.service.seatingEnabled$.pipe(take(1)).subscribe((value) => {
        if (value) this.displayColumns.push('#seating')
      })

      this.event['event_timings'].forEach((element, index) => {
        this.displayColumns.push(`#day${index + 1}`)
      })
    }

    if (form.enable_payment || form.products_count) {
      this.displayColumns.push('#payment')
    }
    if (this._userContext?.isAdmin && (form.enable_payment || form.products_count)) {
      this.displayColumns.push('#refund')
    }
    this.displayColumns.push('#filler')
  }

  /** fetches a new page from the datasource */
  public fetchPage(page: number) {
    // Direct page jumps cannot use the cursor (cursor only walks
    // forward from the last loaded row), so clear it for this request.
    this._lastSortKey = null
    this.initialize({ ...this.initializeRequest, page: page, sortKey: undefined })
  }
  /** loads more entries from the datasource (next page equivalent) */
  public loadMore() {
    if (this.processing) {
      return
    }
    if (this.initializeRequest.maxPages <= this.initializeRequest.page) {
      return
    }
    // Cursor-mode fast-path when the server returned a keyset cursor
    // on the previous page. The backend skips the skip()+limit()+count
    // round-trips entirely and serves the next slice straight off the
    // created_at compound index, so latency stays flat regardless of
    // scroll depth. Falls back to legacy page-based pagination
    // automatically if last_sort_key wasn't returned.
    this.initialize(
      {
        ...this.initializeRequest,
        page: this.initializeRequest.page + 1,
        sortKey: this._lastSortKey || undefined
      },
      true,
      true
    )
  }

  /**
   * Set dynamic field filters and sort, then refresh data
   * Special handling: Day column filters (#day1, #day2, etc.) are converted to days[] and attended format
   */
  public setFieldFilters(fieldFilters: { [key: string]: { values: string[]; inverted?: boolean } }, sortField: string | null, sortDirection: 'asc' | 'desc') {
    // Separate day column filters from regular field filters
    const dayFilters: { [key: string]: { values: string[]; inverted?: boolean } } = {}
    const regularFieldFilters: { [key: string]: { values: string[]; inverted?: boolean } } = {}
    const attendedDays: string[] = []
    const missedDays: string[] = []

    Object.entries(fieldFilters).forEach(([field, filterConfig]) => {
      // Check if this is a day column filter (#day1, #day2, etc.)
      const dayMatch = field.match(/^#day(\d+)$/)
      if (dayMatch && this.event?.event_timings) {
        const dayIndex = parseInt(dayMatch[1], 10) - 1 // Convert #day1 to index 0
        if (dayIndex >= 0 && dayIndex < this.event.event_timings.length) {
          const dayTiming = this.event.event_timings[dayIndex]
          if (dayTiming?.start_datetime) {
            // Convert date to yyyy-MM-dd format
            const dayDate = new Date(dayTiming.start_datetime).toISOString().split('T')[0]

            // Process filter values
            filterConfig.values.forEach((value) => {
              if (value === 'attended') {
                attendedDays.push(dayDate)
              } else if (value === 'missed') {
                missedDays.push(dayDate)
              }
            })
          }
        }
        // Don't add day filters to regular fieldFilters
      } else {
        // Regular field filter
        regularFieldFilters[field] = filterConfig
      }
    })

    // When day filters are active, we use days[] and attended format instead of field_filters
    // This also means search parameter should NOT be sent in bulk actions
    if (attendedDays.length > 0 || missedDays.length > 0) {
      // Store both attended and missed days separately for proper API handling
      this.initializeRequest.attendedDays = Array.from(new Set(attendedDays))
      this.initializeRequest.missedDays = Array.from(new Set(missedDays))

      // Also set days and attended for backward compatibility
      const allDays = Array.from(new Set([...attendedDays, ...missedDays]))
      this.initializeRequest.days = allDays

      // Set attended status - if both are selected, we'll handle both in buildRegistrationsSearchQuery
      if (attendedDays.length > 0 && missedDays.length > 0) {
        // Both selected - set attended to 'yes' but we'll send both filters
        this.initializeRequest.attended = 'yes'
      } else if (attendedDays.length > 0) {
        this.initializeRequest.attended = 'yes'
      } else if (missedDays.length > 0) {
        this.initializeRequest.attended = 'no'
      }
    } else {
      // Clear days filter if no day filters are active
      this.initializeRequest.days = undefined
      this.initializeRequest.attended = undefined
      this.initializeRequest.attendedDays = undefined
      this.initializeRequest.missedDays = undefined
    }

    this.initializeRequest.fieldFilters = regularFieldFilters
    this.initializeRequest.sortField = sortField
    this.initializeRequest.sortDirection = sortDirection
    // Reset to page 1 when filters change
    this.initializeRequest.page = 1
    // Filter / sort change invalidates the previously-collected cursor
    // since the result set is different. Clear it so the next
    // initialize() falls back to the page-1 legacy query (which is the
    // only way to get a fresh total/last_page anyway).
    this.initializeRequest.sortKey = undefined
    this._lastSortKey = null
    this.initialize(this.initializeRequest, true, false)
  }

  /** gets the current page */
  get currentPage() {
    return this.initializeRequest.page
  }
  /** gets the selection status, everything, some, none selected */
  get selectionStatus(): 'All' | 'Some' | 'None' {
    // If allSelected flag is set and no items are explicitly deselected, status is 'All'
    if (this.allSelected && !this.data.some((o) => o.selected === false)) {
      return 'All'
    }
    if (this.data.every((o) => o.selected === false)) {
      return 'None'
    } else if (this.data.every((o) => o.selected === true)) {
      return 'All'
    } else {
      return 'Some'
    }
  }

  get childSelectionStatus(): 'All' | 'Some' | 'None' {
    // If childAllSelected flag is set and no items are explicitly deselected, status is 'All'
    if (this.childAllSelected && !this.childData.some((o) => o.selected === false)) {
      return 'All'
    }
    if (this.childData.every((o) => o.selected === false)) {
      return 'None'
    } else if (this.childData.every((o) => o.selected === true)) {
      return 'All'
    } else {
      return 'Some'
    }
  }

  /** returns the selection array length */
  public get selectionCount() {
    if (this.hasFilters) {
      // When filters are active and all is selected, use the API's total count minus deselections
      return this.allSelected ? this.registrationsCount - this.notSelectedRegistrants.length : this.selectedRegistrants.length
    } else if (this.selectionStatus === 'All') {
      return this.allSelected ? this.registrationsCount - this.notSelectedRegistrants.length : this.selectedRegistrants.length
    } else if (this.selectionStatus === 'Some') {
      return this.allSelected ? this.count - this.notSelectedRegistrants.length : this.selectedRegistrants.length
    } else {
      return 0
    }
  }

  public get childSelectionCount() {
    if (this.childSelectionStatus === 'All') {
      return this.childAllSelected ? this.childDatalength - this.notSelectedChildRegistrants.length : this.selectedChildRegistrants.length
    } else if (this.childSelectionStatus === 'Some') {
      return this.childAllSelected ? this.childDatalength - this.notSelectedChildRegistrants.length : this.selectedChildRegistrants.length
    } else {
      return 0
    }
  }

  get isChildSelected() {
    return this.childData.some((o) => o.selected)
  }

  /** Selects/ toggles items in a datasource */
  public toggleSelection() {
    // Set the allSelected flag immediately - this is the source of truth
    if (this.selectionStatus === 'Some') {
      this.allSelected = true
    } else if (this.selectionStatus === 'All') {
      this.allSelected = false
    } else {
      this.allSelected = true
    }

    // Only update rows that need updating for performance
    if (this.allSelected) {
      // When selecting all, set any non-true rows to true (includes false and undefined)
      // This ensures notSelectedRegistrants.length is 0 for bulk action 'all'
      const needsUpdate = this.data.filter((o) => o.selected !== true)
      needsUpdate.forEach((o) => (o.selected = true))
    } else {
      // When deselecting all, mark all as deselected
      this.data.forEach((o) => (o.selected = false))
    }

    // Notify observers immediately - visual updates happen via template binding
    this.changed$.next(null)
    if (this._data$) {
      this._data$.next(this.data)
    }

    // Note: Template binding checks `isRowSelected()` which considers allSelected flag.
    // Rows with non-true values are updated to ensure bulk operations work correctly.
    // Newly loaded rows via initialize/append automatically get correct state via lines 122-131
  }

  public toggleSelectionChild(event: any) {
    // Set the childAllSelected flag immediately - this is the source of truth
    if (this.childSelectionStatus === 'Some') {
      this.childAllSelected = true
    } else if (this.childSelectionStatus === 'All') {
      this.childAllSelected = false
    } else if (this.childSelectionStatus === 'None') {
      this.childAllSelected = true
    }

    // Only update child rows that need updating for performance
    if (this.childAllSelected) {
      // When selecting all children, set any non-true rows to true (includes false and undefined)
      const needsUpdate = this.childData.filter((o) => o.selected !== true)
      needsUpdate.forEach((o) => (o.selected = true))
    } else {
      // When deselecting all children, mark all as deselected
      this.childData.forEach((o) => (o.selected = false))
    }
  }
  /** clears all datasource selections */
  public clearSelection() {
    this.allSelected = false
    this.data.forEach((o) => o.toggleSelect(false))
  }
  /** list of selected entries */
  get selectedRegistrants(): Registration[] {
    // When allSelected is true and no deselections, return all data without filtering
    if (this.allSelected && !this.data.some((o) => o.selected === false)) {
      return this.data
    }
    return this.data.filter((o) => o.selected)
  }

  get selectedChildRegistrants(): Registration[] {
    // When childAllSelected is true and no deselections, return all child data without filtering
    if (this.childAllSelected && !this.childData.some((o) => o.selected === false)) {
      return this.childData
    }
    return this.childData.filter((o) => o.selected)
  }

  get notSelectedChildRegistrants(): Registration[] {
    // Filter for explicitly deselected children (selected === false)
    return this.childData.filter((o) => o.selected === false)
  }

  /** list of non-selected entries */
  get notSelectedRegistrants(): Registration[] {
    // Filter for explicitly deselected rows (selected === false)
    // Don't count undefined/null as deselected when allSelected is true
    return this.data.filter((o) => o.selected === false)
  }
  /** unhides a certain field in the datasource that belongs to a registrations, and hides other visible fields */
  public unhide(registration: Registration, fieldName: string) {
    // Regenerate all parent registrations' fields to hide any previously revealed fields
    this.data.forEach((o) => o.generateFieldsAndCalculatedFields())

    // Regenerate all child registrations' fields to hide any previously revealed fields
    if (this.childData && this.childData.length > 0) {
      this.childData.forEach((child) => child.generateFieldsAndCalculatedFields())
    }

    registration.unhide(fieldName)
  }

  /** based on selection status - returns the bulk operation mode */
  public get bulkOperationAction() {
    let action: 'all' | 'include' | 'exclude' | 'test' = 'include'
    if (this.allSelected) {
      action = 'all'
      // If there are deselections, switch to 'exclude' mode
      if (this.notSelectedRegistrants.length) {
        action = 'exclude'
      }
    }
    return action
  }

  public get bulkOperationChildAction(): 'all' | 'include' | 'exclude' | 'parent' {
    let action: 'all' | 'include' | 'exclude' | 'parent' = 'include'
    if (this.childAllSelected) {
      action = 'parent'
      if (this.notSelectedChildRegistrants.length) {
        action = 'exclude'
      }
    }
    return action
  }

  /** based on bulk operation status returns a list of registrants */
  public get bulkOperationRegistrants() {
    return this.bulkOperationAction === 'all' ? this.selectedRegistrants : this.bulkOperationAction === 'include' ? this.selectedRegistrants : this.notSelectedRegistrants
  }

  public get bulkOperationChildRegistrants() {
    return this.bulkOperationChildAction === 'all'
      ? this.selectedChildRegistrants
      : this.bulkOperationChildAction === 'include'
      ? this.selectedChildRegistrants
      : this.notSelectedChildRegistrants
  }
  /** based on bulk operation status returns a mapped list of registrant ids */
  public get bulkOperationIds() {
    return this.bulkOperationAction === 'all'
      ? []
      : this.bulkOperationAction === 'include'
      ? this.selectedRegistrants.map((o) => o.id)
      : this.notSelectedRegistrants.map((o) => o.id)
  }

  public get bulkOperationChildIds() {
    return resolveBulkChildIds(
      this.bulkOperationChildAction as BulkChildAction,
      this.currentParentId,
      this.selectedChildRegistrants.map((o) => o.id),
      this.notSelectedChildRegistrants.map((o) => o.id)
    )
  }

  /** Approves selected entries */
  public approveSelection(status = ApprovalStatus.APPROVED) {
    if (this.childSelectionCount) {
      this.approveChildSelection(status)
    } else {
      this.approveParentSelection(status)
    }
  }

  /**
   * Approves multiple selected child registrations
   * @param status - The approval status to change to (default: APPROVED)
   * Optimistically updates both entry_status and approvedBy fields for immediate UI feedback
   */
  public approveChildSelection(status = ApprovalStatus.APPROVED) {
    this.processing = true
    this.changed$.next(null)

    // Store original states for rollback
    const originalStates = new Map()
    const registrantsToUpdate = ['exclude', 'parent'].includes(this.bulkOperationChildAction) ? this.selectedChildRegistrants : this.bulkOperationChildRegistrants

    // Get current user for approvedBy / declinedBy fields (from AuthService context)
    const currentUser = status === ApprovalStatus.APPROVED || status === ApprovalStatus.DECLINED ? this._userContext?.approvedBy ?? null : null

    // Optimistic update - immediately update UI
    registrantsToUpdate.forEach((registration) => {
      originalStates.set(registration.id, {
        entry_status: registration.entry_status,
        approvedBy: registration.approvedBy,
        declinedBy: registration.declinedBy
      })
      if (
        this.childForm.require_approval &&
        this.childForm.enable_payment &&
        registration.payments.length == 0 &&
        (registration.entry_status === 0 || registration.entry_status === 1)
      ) {
        registration.entry_status = ApprovalStatus.PENDING_PAYMENT
      } else {
        registration.entry_status = status
      }

      if (status === ApprovalStatus.APPROVED && currentUser) {
        registration.approvedBy = currentUser
      }
      if (status === ApprovalStatus.DECLINED && currentUser) {
        registration.declinedBy = currentUser
      }
    })
    // Ensure childForm is available before making API call
    if (!this._ensureChildFormInitialized()) {
      this.service.alert(Alert.ERROR, 'Child form slug is not available. Cannot approve child registrations.')
      this.processing = false
      this.changed$.next(null)
      return
    }

    this.service
      .bulkApproveRegistrations(
        this.initializeRequest.event,
        this.childForm.slug,
        this.bulkOperationChildIds,
        status,
        this.bulkOperationChildAction,
        this._CountOperationChildRegistrants(this.bulkOperationChildIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        () => {
          this.processing = false
          this.changed$.next(null)
        },
        () => {
          // Rollback optimistic updates on failure
          registrantsToUpdate.forEach((registration) => {
            const original = originalStates.get(registration.id)
            registration.entry_status = original.entry_status
            registration.approvedBy = original.approvedBy
            registration.declinedBy = original.declinedBy
          })
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  /**
   * Approves multiple selected parent registrations
   * @param status - The approval status to change to (default: APPROVED)
   * Optimistically updates both entry_status and approvedBy fields for immediate UI feedback
   */
  public approveParentSelection(status = ApprovalStatus.APPROVED) {
    this.processing = true
    this.changed$.next(null)

    // Store original states for rollback
    const originalStates = new Map()

    // Get current user for approvedBy / declinedBy fields (from AuthService context)
    const currentUser = status === ApprovalStatus.APPROVED || status === ApprovalStatus.DECLINED ? this._userContext?.approvedBy ?? null : null

    // Optimistic update - immediately update UI
    this.selectedRegistrants.forEach((registration) => {
      originalStates.set(registration.id, {
        entry_status: registration.entry_status,
        approvedBy: registration.approvedBy,
        declinedBy: registration.declinedBy
      })
      if (this.form.require_approval && this.form.enable_payment && registration.payments.length == 0 && (registration.entry_status === 0 || registration.entry_status === 1)) {
        registration.entry_status = ApprovalStatus.PENDING_PAYMENT
      } else {
        registration.entry_status = status
      }

      if (status === ApprovalStatus.APPROVED && currentUser) {
        registration.approvedBy = currentUser
      }
      if (status === ApprovalStatus.DECLINED && currentUser) {
        registration.declinedBy = currentUser
      }
    })

    this.changed$.next(null)

    this.service
      .bulkApproveRegistrations(
        this.initializeRequest.event,
        this.initializeRequest.form,
        this.bulkOperationIds,
        status,
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        undefined,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        () => {
          this.processing = false
          this.changed$.next(null)
        },
        () => {
          // Rollback optimistic updates on failure
          this.selectedRegistrants.forEach((registration) => {
            const original = originalStates.get(registration.id)
            if (original) {
              registration.entry_status = original.entry_status
              registration.approvedBy = original.approvedBy
              registration.declinedBy = original.declinedBy
            }
          })
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  /**
   * Changes the status of a child registration
   * @param status - The approval status to change to
   * @param registrant - The child registration to update
   * When status is APPROVED, optimistically updates the approvedBy field with current user's name from localStorage
   */
  public changeStatusChild(status, registrant) {
    const current = registrant.entry_status
    const previousApprovedBy = registrant.approvedBy
    const previousDeclinedBy = registrant.declinedBy
    this.processing = true
    registrant.entry_status = status

    if (status === ApprovalStatus.APPROVED && this._userContext?.approvedBy) {
      registrant.approvedBy = this._userContext.approvedBy
    }
    if (status === ApprovalStatus.DECLINED && this._userContext?.approvedBy) {
      registrant.declinedBy = this._userContext.approvedBy
    }

    this.changed$.next(null)

    // Ensure childForm is available before making API call
    if (!this._ensureChildFormInitialized()) {
      this.service.alert(Alert.ERROR, 'Child form slug is not available. Cannot change child registration status.')
      registrant.entry_status = current
      registrant.approvedBy = previousApprovedBy
      registrant.declinedBy = previousDeclinedBy
      this.processing = false
      this.changed$.next(null)
      return
    }

    this.service.approveRegistration(this.initializeRequest.event, this.childForm.slug, registrant.id, status).subscribe(
      () => {
        this.processing = false
        this.changed$.next(null)
      },
      () => {
        registrant.entry_status = current
        registrant.approvedBy = previousApprovedBy
        registrant.declinedBy = previousDeclinedBy
        this.processing = false
        this.changed$.next(null)
      }
    )
  }
  /**
   * Mark attended selected entries
   */
  public markBulkAttended() {
    if (this.childSelectionCount) {
      this.markBulkAttendedChild()
    } else {
      this.markBulkAttendedParent()
    }
  }

  public markBulkAttendedChild() {
    this.processing = true
    this.changed$.next(null)

    // ⚠️ REMOVED OPTIMISTIC UPDATE - Let bulk progress toast handle status
    const registrantsToUpdate = ['exclude', 'parent'].includes(this.bulkOperationChildAction) ? this.selectedChildRegistrants : this.bulkOperationChildRegistrants
    // Ensure childForm is available before making API call
    if (!this._ensureChildFormInitialized()) {
      this.service.alert(Alert.ERROR, 'Child form slug is not available. Cannot mark child registrations as attended.')
      this.processing = false
      this.changed$.next(null)
      return
    }

    this.service
      .bulkAttended(
        this.initializeRequest.event,
        this.childForm.slug,
        this.bulkOperationChildIds,
        'global',
        this.bulkOperationChildAction,
        this._CountOperationChildRegistrants(this.bulkOperationChildIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        (res) => {
          // ✅ Operation queued successfully
          // Mark attended operation queued - progress shown in bottom-left indicator
          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.service.toastrService.error('Failed to queue mark attended operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  public markBulkAttendedParent() {
    this.processing = true
    this.changed$.next(null)
    this.service
      .bulkAttended(
        this.initializeRequest.event,
        this.initializeRequest.form,
        this.bulkOperationIds,
        'global',
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        undefined,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        (res) => {
          // ✅ Optimistic UI update: Mark as attended (original logic restored)
          const updateAttendance = (registrants) => {
            registrants.forEach((registration) => {
              if (!Array.isArray(registration.attendanceDays)) {
                registration.attendanceDays = []
              }
              registration.attendanceDays.push(new Date().toISOString().slice(0, 10))
              registration.attended = true
            })
          }

          updateAttendance(this.bulkOperationAction === 'exclude' ? this.selectedRegistrants : this.bulkOperationRegistrants)

          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.service.toastrService.error('Failed to queue mark attended operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }
  /**
   * Mark unattended selected entries
   */
  public markBulkUnttended() {
    if (this.childSelectionCount) {
      this.markBulkChildUnttended()
    } else {
      this.markParentUnattend()
    }
  }

  public markBulkChildUnttended() {
    this.processing = true
    this.changed$.next(null)

    // ⚠️ REMOVED OPTIMISTIC UPDATE - Let bulk progress toast handle status
    const registrantsToUpdate = ['exclude', 'parent'].includes(this.bulkOperationChildAction) ? this.selectedChildRegistrants : this.bulkOperationChildRegistrants
    // Ensure childForm is available before making API call
    if (!this._ensureChildFormInitialized()) {
      this.service.alert(Alert.ERROR, 'Child form slug is not available. Cannot mark child registrations as unattended.')
      this.processing = false
      this.changed$.next(null)
      return
    }

    this.service
      .bulkUnattend(
        this.initializeRequest.event,
        this.childForm.slug,
        this.bulkOperationChildIds,
        this.bulkOperationChildAction,
        this._CountOperationChildRegistrants(this.bulkOperationChildIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        (res) => {
          // ✅ Operation queued successfully
          // Mark unattended operation queued - progress shown in bottom-left indicator
          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.service.toastrService.error('Failed to queue mark unattended operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  public markParentUnattend() {
    this.processing = true
    this.changed$.next(null)
    this.service
      .bulkUnattend(
        this.initializeRequest.event,
        this.initializeRequest.form,
        this.bulkOperationIds,
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        undefined,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        (res) => {
          // ✅ Optimistic UI update: Mark as unattended (original logic restored)
          const updateAttendance = (registrants) => {
            registrants.forEach((registration) => {
              registration.attended = false
              registration.attendanceDays = []
            })
          }

          updateAttendance(this.bulkOperationAction === 'exclude' ? this.selectedRegistrants : this.bulkOperationRegistrants)

          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  /** disapproves selected entries */
  public disapproveSelection() {
    this.approveSelection(ApprovalStatus.DECLINED)
  }
  /** flags selected entires */
  public flagSelection(flag = true) {
    if (!this.selectedRegistrants.length) {
      return
    }
    this.processing = true
    this.changed$.next(null)
    this.service
      .bulkFlagRegistrations(
        this.initializeRequest.event,
        this.initializeRequest.form,
        this.bulkOperationIds,
        flag,
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        () => {
          // ✅ Optimistic UI update: Update flag status (original logic restored)
          this.bulkOperationRegistrants.forEach((registration) => (registration.flagged = flag))

          const actionName = flag ? 'Flag' : 'Unflag'
          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          const actionName = flag ? 'flag' : 'unflag'
          this.service.toastrService.error(`Failed to queue ${actionName} operation`, 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }
  /** unflags selected entries */
  public unflagSelection() {
    this.flagSelection(false)
  }
  /** deletes selected entries */
  public deleteSelection() {
    if (this.childSelectionCount) {
      this.deleteChildSelection()
    } else {
      this.deleteParentSelection()
    }
  }

  deleteChildSelection() {
    if (!this.selectedChildRegistrants.length) {
      return
    }

    // Ensure childForm is available before making API call
    if (!this._ensureChildFormInitialized()) {
      this.service.alert(Alert.ERROR, 'Child form slug is not available. Cannot delete child registrations.')
      return
    }

    const removedIds = this.bulkOperationChildIds
    const count = this._CountOperationChildRegistrants(this.bulkOperationChildIds)
    this.service
      .bulkDeleteRegistrations(
        this.initializeRequest.event,
        this.childForm.slug,
        removedIds,
        this.bulkOperationChildAction,
        this._CountOperationChildRegistrants(this.bulkOperationChildIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        () => {
          // ✅ Optimistic UI update: Remove deleted items (original logic restored)
          if (this.bulkOperationChildAction === 'exclude') {
            this.childData = this.childData.filter((data) => !this.selectedChildRegistrants.some((reg) => reg.id === data.id))
          } else {
            this.childData = removedIds.length && this.bulkOperationChildAction !== 'all' ? this.childData.filter((o) => !removedIds.includes(o.id)) : []
          }

          this.childAllSelected = false
          this.childDatalength -= count
          this.childDataSource = new MatTableDataSource<any>(this.childData)
          this.processing = false
          this.changed$.next(null)
        },
        (error) => {
          this.service.toastrService.error('Failed to queue delete operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  deleteParentSelection() {
    if (!this.selectedRegistrants.length) {
      return
    }
    this.processing = true
    this.changed$.next(null)
    const removedIds = this.bulkOperationIds
    const count = this._CountOperationRegistrants(this.bulkOperationIds)
    this.service
      .bulkDeleteRegistrations(
        this.initializeRequest.event,
        this.initializeRequest.form,
        removedIds,
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        undefined,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .subscribe(
        () => {
          // ✅ Optimistic UI update: Remove deleted items (original logic restored)
          if (this.bulkOperationAction === 'exclude') {
            this.data = this.data.filter((data) => !this.selectedRegistrants.some((reg) => reg.id === data.id))
          } else {
            this.data = removedIds.length && this.bulkOperationAction !== 'all' ? this.data.filter((o) => !removedIds.includes(o.id)) : []
          }

          this.allSelected = false
          this.registrationsCount -= count
          this._data$.next(this.data)
          this.processing = false
          this.changed$.next(null)
        },
        (error) => {
          this.service.toastrService.error('Failed to queue delete operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  /**
   * Connects a collection viewer (such as a data-table) to this data source. Note that
   * the stream provided will be accessed during change detection and should not directly change
   * values that are bound in template views.
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   * @returns Observable that emits a new value when the data changes.
   */
  public connect(collectionViewer: CollectionViewer): Observable<Registration[] | ReadonlyArray<Registration>> {
    return this._data$.asObservable()
  }
  /**
   * Disconnects a collection viewer (such as a data-table) from this data source. Can be used
   * to perform any clean-up or tear-down operations when a view is being destroyed.
   *
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   */
  public disconnect(collectionViewer: CollectionViewer): void {
    this._data$.complete()
    this._data$ = null
    this.destroy()
  }

  /**
   * Ensures childForm is properly initialized before performing child operations
   * @returns boolean indicating if childForm is available
   */
  private _ensureChildFormInitialized(): boolean {
    if (this.childForm?.slug) {
      return true
    }

    // Try to find and initialize childForm if not already set
    if (this.form?.type === 30) {
      // GROUP form
      // This would need access to the forms array, but since we don't have it here,
      // we'll rely on the component to properly initialize childForm
      this.service.alert(Alert.WARNING, 'Child form not initialized. Please ensure childForm is set before performing child operations.')
      return false
    }

    return false
  }

  /**
   * Count the number of registrants to be affected by the bulk operation
   * @param ids
   * @returns number of registrants to be affected by the bulk operation
   */
  private _CountOperationRegistrants(ids: string[]) {
    switch (this.bulkOperationAction) {
      case 'all':
        return this.registrationsCount
      case 'include':
        return this.selectedRegistrants.length
      default:
        return this.registrationsCount - ids.length
    }
  }
  private _CountOperationChildRegistrants(ids: string[]) {
    return countBulkChildSelection(this.bulkOperationChildAction as BulkChildAction, this.childDatalength, this.selectedChildRegistrants.length, ids.length)
  }

  /**
   * Request revision for selected entries
   */
  public requestRevisionSelection() {
    if (this.childSelectionCount) {
      this._requestRevisionChildSelection()
    } else {
      this._requestRevisionNormalPersonaSelection()
    }
  }

  /**
   * Request revision for child selected entries
   */
  private _requestRevisionChildSelection() {
    this.processing = true
    this.changed$.next(null)

    // ⚠️ REMOVED OPTIMISTIC UPDATE - Let bulk progress toast handle status
    const registrantsToUpdate = ['exclude', 'parent'].includes(this.bulkOperationChildAction) ? this.selectedChildRegistrants : this.bulkOperationChildRegistrants

    this.service
      .bulkApproveRegistrations(
        this.initializeRequest.event,
        this.childForm.slug,
        this.bulkOperationChildIds,
        ApprovalStatus.AWAITING_REVISION,
        this.bulkOperationChildAction,
        this._CountOperationChildRegistrants(this.bulkOperationChildIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          // ✅ Operation queued successfully
          // Request revision operation queued - progress shown in bottom-left indicator
          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.service.toastrService.error('Failed to queue request revision operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }

  /**
   * Request revision for normal persona selected entries
   */
  private _requestRevisionNormalPersonaSelection() {
    this.processing = true
    this.changed$.next(null)

    // ⚠️ REMOVED OPTIMISTIC UPDATE - Let bulk progress toast handle status
    const registrantsToUpdate = this.bulkOperationAction === 'exclude' ? this.selectedRegistrants : this.bulkOperationRegistrants

    this.service
      .bulkApproveRegistrations(
        this.initializeRequest.event,
        this.initializeRequest.form,
        this.bulkOperationIds,
        ApprovalStatus.AWAITING_REVISION,
        this.bulkOperationAction,
        this._CountOperationRegistrants(this.bulkOperationIds),
        this.currentParentId,
        this.service.buildBulkRegistrationsSearchQuery(this.initializeRequest)
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          // ✅ Operation queued successfully
          // Request revision operation queued - progress shown in bottom-left indicator
          this.processing = false
          this.changed$.next(null)
        },
        (err) => {
          this.service.toastrService.error('Failed to queue request revision operation', 'Please try again')
          this.processing = false
          this.changed$.next(null)
        }
      )
  }
}
